import { FC, useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Add, KeyboardArrowDown, Download, Upload } from "@mui/icons-material";
import { toast } from "react-toastify";
import DashboardContentLayout from "../components/dashboard-content-layout";
import FilterRow from "../components/generic-table/filter-row";
import sanitizeFilterValue from "../../../utils/sanitize-filter-value";
import { ControlledSelectOption, useDebounce } from "ui";
import {
  Nullable,
  ServiceTypeEnum,
  serviceTypeEnumValues,
  useAdminCustomerLocationControllerGetAll,
  useAdminLocationControllerGetAllCSV,
  useAdminLocationControllerGetCsvTemplate,
  useAdminLocationControllerCreateFromCsv,
  useAdminLocationControllerAcceptUpdatesFromCsv,
  AdminLocationControllerCreateFromCsvRequestBody,
} from "service";
import { serviceTypeSelectOptions } from "service/types/enum/service-type-enum";
import { isNil } from "lodash";
import AddressManagementTableCombo from "./address-management-table";
import PlowReportDialog from "./plow-report-dialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortOptions } from "../components/generic-table/generic-table";
import { useSocketEvents } from "../../../web-sockets/use-socket-events";
import { AdRoutes } from "../../../constants/ad-routes";
import EmptyState, {
  noResultsEmptyState,
} from "@/shared/components/EmptyState";
import { useListEmptyState } from "@/shared/hooks/useListEmptyState";
import MoreResultsLink from "@/shared/components/MoreResultsLink";
import { useMatchingLeadsCount } from "@/hooks/use-cross-entity-matches";
import CustomerBulkActions from "./customer-bulk-actions";
import { addressIso } from "../../../utils/string-helpers";
import { localFileTimestamp } from "../../../utils/date-time";
import { AcceptChangesFromCsvDto } from "service/api/estabrookSchemas";
import useCsvUploadStore from "../../../state/csv-upload-store";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import HeaderStandardActions from "../../../shared/components/header-items/HeaderStandardActions";
import { UserDownloadIcon } from "ui";
import { usePersistedSearchParams } from "../../../hooks/use-persisted-search-params";

const booleanOptions: ControlledSelectOption[] = [
  {
    label: "Yes",
    value: "true",
  },
  {
    label: "No",
    value: "false",
  },
];

const CustomerManagementComboPage: FC = () => {
  const { debounce } = useDebounce(475);
  const navigate = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const {
    startUpload,
    resetVisualState,
    setModalStatus,
    setUpdateCustomersCallback,
    setAcceptingFromMainPage,
  } = useCsvUploadStore();

  // Persist search params for breadcrumb navigation
  usePersistedSearchParams();

  const [page, setPage] = useState<number>(
    params.get("page") ? parseInt(params.get("page") as string) : 1,
  );
  const [search, setSearch] = useState<string>(params.get("search") || "");
  const [limit, setLimit] = useState<number>(
    params.get("limit") ? parseInt(params.get("limit") as string) : 50,
  );

  const [sortOptions, setSortOptions] = useState<Nullable<SortOptions>>(null);
  const [serviceType, setServiceType] = useState<ServiceTypeEnum | undefined>(
    undefined,
  );
  const [snowReporter, setSnowReporter] = useState<boolean | undefined>(
    undefined,
  );
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [csvMenuAnchorEl, setCsvMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const csvMenuOpen = Boolean(csvMenuAnchorEl);

  // Note: CSV Import Modal states now managed by store
  // csvImportModalOpen -> isModalOpen from store
  // csvImportStatus -> modalStatus from store
  // csvReviewData -> reviewData from store

  // Track when user explicitly requested downloads
  const [csvDownloadRequested, setCsvDownloadRequested] = useState(false);
  const [templateDownloadRequested, setTemplateDownloadRequested] =
    useState(false);

  // Note: Window event listeners for CSV modal removed - now handled by store

  const { mutate: uploadCsvFile, isLoading: isUploadingCSV } =
    useAdminLocationControllerCreateFromCsv({
      onSuccess: (response) => {
        if (response?.data) {
          startUpload("Processing Customer Upload");
        }
      },
      onError: (error) => {
        console.error("CSV upload failed:", error);
        toast.error("Failed to upload CSV file. Please try again.");
      },
    });

  const { mutate: acceptCsvUpdates } =
    useAdminLocationControllerAcceptUpdatesFromCsv({
      onSuccess: () => {
        // Reset visual state immediately but allow WebSocket completion to fire
        resetVisualState();
        // Don't close modal here - wait for socket event
        setModalStatus("updating");
      },
      onError: (error) => {
        console.error("CSV accept failed:", error);
        // Reset the flag on error
        setAcceptingFromMainPage(false);
        toast.error("Failed to apply CSV updates. Please try again.");
        // Modal will be closed by the store on error
      },
    });

  const handleCsvMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCsvMenuAnchorEl(event.currentTarget);
  };

  const handleCsvMenuClose = () => {
    setCsvMenuAnchorEl(null);
  };

  // Note: Modal close is now handled by the store

  const handleUpdateCustomers = useCallback(
    (selections: AcceptChangesFromCsvDto) => {
      acceptCsvUpdates({
        body: selections,
      });
    },
    [acceptCsvUpdates, setAcceptingFromMainPage],
  );

  // Set up the callback for CSV updates when component mounts
  useEffect(() => {
    setUpdateCustomersCallback(handleUpdateCustomers);

    // Cleanup callback when component unmounts
    return () => {
      setUpdateCustomersCallback(() => {
        // No-op function for cleanup
      });
    };
  }, [setUpdateCustomersCallback, handleUpdateCustomers]);

  const handleDownloadTemplate = () => {
    setTemplateDownloadRequested(true);
    execTemplateDownload();
    handleCsvMenuClose();
  };

  const handleUploadCSV = () => {
    // Create a file input element
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".csv";
    fileInput.style.display = "none";

    fileInput.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        if (
          file.type === "text/csv" ||
          file.name.toLowerCase().endsWith(".csv")
        ) {
          // Create FormData to properly send the file
          const formData = new FormData();
          formData.append("file", file);

          // Upload the file - cast to work around API type limitations
          uploadCsvFile({
            body: formData as unknown as AdminLocationControllerCreateFromCsvRequestBody,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        } else {
          toast.error("Please select a valid CSV file");
        }
      }
    };

    // Trigger file selection
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);

    handleCsvMenuClose();
  };

  const onSearch = (searchValue: string) => {
    debounce(() => {
      setSearch(searchValue);
      // Reset to page 1 when search changes
      setPage(1);
      // Note: FilterRow already updates the 'search' param in the URL
    });
  };

  // Track previous values to avoid unnecessary URL updates on mount
  const prevPageRef = useRef(page);
  const prevLimitRef = useRef(limit);

  // Sync page and limit changes to URL (only when they actually change)
  useEffect(() => {
    // Skip if values haven't changed from their previous state
    if (prevPageRef.current === page && prevLimitRef.current === limit) {
      return;
    }
    prevPageRef.current = page;
    prevLimitRef.current = limit;

    setSearchParams((prev) => {
      if (page > 1) {
        prev.set("page", page.toString());
      } else {
        prev.delete("page");
      }
      if (limit !== 50) {
        prev.set("limit", limit.toString());
      } else {
        prev.delete("limit");
      }
      return prev;
    });
  }, [page, limit, setSearchParams]);

  const queryParams = {
    ...(page && { page }),
    ...(limit && { limit }),
    ...(search && { search }),
    ...(sortOptions?.sortBy && {
      sort: sortOptions?.sortBy as
        | "fullName"
        | "address"
        | "emailConfirmed"
        | "reportsEnabled"
        | "region"
        | "serviceTime"
        | "elevation"
        | undefined,
    }),
    ...(sortOptions?.direction && { order: sortOptions.direction }),
    ...(serviceType && {
      type: serviceType,
    }),
    ...(snowReporter !== undefined && {
      reportsEnabled: snowReporter,
    }),
  };

  const { isLoading, isError, refetch, data } =
    useAdminCustomerLocationControllerGetAll({
      queryParams,
    });

  // surface matches on the Leads table for the same search term.
  const matchingLeadsCount = useMatchingLeadsCount(search);

  const hasActiveFilters =
    Boolean(search) || Boolean(serviceType) || !isNil(snowReporter);
  const { isEmpty, isTrueEmpty } = useListEmptyState(
    isLoading,
    data?.metaData?.count ?? 0,
    hasActiveFilters,
    isError,
  );

  const clearFilters = () => {
    setSearch("");
    setServiceType(undefined);
    setSnowReporter(undefined);
    setPage(1);
    setSearchParams((prev) => {
      prev.delete("search");
      prev.delete("page");
      return prev;
    });
  };

  const handleCsvImportCompleted = useCallback(() => {
    // Modal will be closed by the store when upload completes
    refetch();
    toast.success("Customer data updated successfully!");
  }, [refetch]);

  // WebSocket events for CSV sync
  useSocketEvents({
    currentPage: "customer-management",
    setCsvImportStatus: (status) => {
      // Check if upload has been completed - if so, ignore status updates
      const currentState = useCsvUploadStore.getState();
      if (currentState.status === "idle" && !currentState.reviewData) {
        return;
      }

      setModalStatus(status);
      // Don't automatically open modal - only when explicitly requested via sidebar
    },
    setCsvReviewData: () => {
      // Check if upload has been completed - if so, ignore review data updates
      const currentState = useCsvUploadStore.getState();
      if (currentState.status === "idle" && !currentState.reviewData) {
        return;
      }

      // Note: Review data is now managed through store
      // Socket data will be handled by sidebar and passed to store
    },
    onCsvImportCompleted: handleCsvImportCompleted,
  });

  const {
    refetch: execDownload,
    data: csvData,
    isLoading: isCSVLoading,
  } = useAdminLocationControllerGetAllCSV(
    {
      queryParams: {
        page,
        limit,
        search,
        sort: sortOptions?.sortBy as "address" | "name" | "emailConfirmed",
        order: sortOptions?.direction,
        type: serviceType,
        reportsEnabled: snowReporter,
      },
      headers: {
        "Content-Type": "text/csv",
      },
    },
    {
      enabled: false,
    },
  );

  const {
    refetch: execTemplateDownload,
    data: csvTemplateData,
    isLoading: isCSVTemplateLoading,
  } = useAdminLocationControllerGetCsvTemplate(
    {
      headers: {
        "Content-Type": "text/csv",
      },
    },
    {
      enabled: false,
    },
  );

  useEffect(() => {
    if (csvData && !isCSVLoading && csvDownloadRequested) {
      // csvData is already a Blob from the fetcher when response is not JSON
      const blob = csvData as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `addresses-${localFileTimestamp()}.csv`;
      a.click();

      // Reset the download request flag after download
      setCsvDownloadRequested(false);
    }
  }, [csvData, isCSVLoading, csvDownloadRequested]);

  useEffect(() => {
    if (csvTemplateData && !isCSVTemplateLoading && templateDownloadRequested) {
      // csvTemplateData is already a Blob from the fetcher when response is not JSON
      const blob = csvTemplateData as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customer-template-${localFileTimestamp()}.csv`;
      a.click();

      // Reset the download request flag after download
      setTemplateDownloadRequested(false);
    }
  }, [csvTemplateData, isCSVTemplateLoading, templateDownloadRequested]);

  const downloadCSV = () => {
    setCsvDownloadRequested(true);
    execDownload();
  };

  const handleCheckboxChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    locationId: string,
  ) => {
    setSelectedLocationIds((prevSelectedLocationIds) => {
      if (evt.target.checked) {
        // Only add if locationId is not already in the array
        return prevSelectedLocationIds.includes(locationId)
          ? prevSelectedLocationIds
          : [...prevSelectedLocationIds, locationId];
      } else {
        // Remove locationId if unchecked
        return prevSelectedLocationIds.filter(
          (selectedLocationId) => selectedLocationId !== locationId,
        );
      }
    });
  };

  const handleCheckboxChangeForAll = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (evt.target.checked) {
      setSelectedLocationIds(
        Array.from(
          new Set(
            (data?.data || [])
              .filter((item) => item.locationId)
              .map((item) => item.locationId),
          ),
        ),
      );
    } else {
      setSelectedLocationIds([]);
    }
  };

  const resetLocationSelections = () => {
    setSelectedLocationIds([]);
  };

  const [plowReport, setPlowReport] = useState<{
    locationIds: string[];
    subtitle: string;
    /** Clear the row selection after a successful download (bulk action only). */
    clearSelectionOnSuccess: boolean;
  } | null>(null);

  const openPlowReportForLocation = (locationId: string, subtitle: string) => {
    setPlowReport({
      locationIds: [locationId],
      subtitle,
      clearSelectionOnSuccess: false,
    });
  };

  const openPlowReportForLocations = (locationIds: string[]) => {
    if (locationIds.length > 0) {
      const subtitle =
        locationIds.length === 1
          ? "1 address"
          : `${locationIds.length} addresses`;
      setPlowReport({ locationIds, subtitle, clearSelectionOnSuccess: true });
    }
  };

  const selectedAddresses = useMemo(() => {
    return selectedLocationIds.map((locationId) => {
      const location = data?.data?.find(
        (item) => item.locationId === locationId,
      );
      return {
        locationId,
        customerId: location?.customerId || "",
        address: location ? addressIso(location) : "",
        customerServiceAccess: location?.appAccess ?? true,
      };
    });
  }, [selectedLocationIds, data?.data]);

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Customer Management",
                url: AdRoutes.customer,
              },
            ]}
          />
          <HeaderItemsRight>
            <HeaderStandardActions
              onRefresh={isTrueEmpty ? undefined : refetch}
              onAdd={() => navigate(AdRoutes.addCustomerCombo)}
              addButtonText="Add Customer"
              customActions={
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCsvMenuClick}
                    endIcon={<KeyboardArrowDown />}
                    aria-controls={csvMenuOpen ? "csv-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={csvMenuOpen ? "true" : undefined}
                  >
                    Update Customers
                  </Button>
                  <Menu
                    id="csv-menu"
                    anchorEl={csvMenuAnchorEl}
                    open={csvMenuOpen}
                    onClose={handleCsvMenuClose}
                    MenuListProps={{
                      "aria-labelledby": "csv-actions-button",
                    }}
                  >
                    <MenuItem onClick={handleDownloadTemplate}>
                      <ListItemIcon>
                        <Download fontSize="small" sx={{ color: "#C1C7CE" }} />
                      </ListItemIcon>
                      <ListItemText>Download CSV Template</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={handleUploadCSV}
                      disabled={isUploadingCSV}
                    >
                      <ListItemIcon>
                        <Upload fontSize="small" sx={{ color: "#C1C7CE" }} />
                      </ListItemIcon>
                      <ListItemText>
                        {isUploadingCSV ? "Uploading..." : "Upload CSV File"}
                      </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={downloadCSV} disabled={isTrueEmpty}>
                      <ListItemIcon>
                        <UserDownloadIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Download Customer List</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              }
              customActionsPlacement="before"
            />
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      {!isTrueEmpty && (
        <FilterRow
          bulkActions={
            <CustomerBulkActions
              locationIds={selectedLocationIds}
              resetSelections={resetLocationSelections}
              selectedAddresses={selectedAddresses}
              onDownloadReport={openPlowReportForLocations}
            />
          }
          tablePaginationProps={{
            limit,
            page,
            isLoading: isLoading,
            count: data?.metaData?.count ?? 10,
            setToPage: setPage,
            setLimit,
          }}
          search={search}
          onSearchChanged={onSearch}
          filters={[
            {
              placeholder: "Service Type",
              options: serviceTypeSelectOptions,
              value: serviceType,
              onChange: (value: string) => {
                const v = sanitizeFilterValue(value, serviceTypeEnumValues);
                setServiceType(v);
              },
            },
            {
              placeholder: "Snow Reporter",
              options: booleanOptions,
              value: isNil(snowReporter) ? undefined : String(snowReporter),
              onChange: (value: string) => {
                if (value === "none") {
                  setSnowReporter(undefined);
                  return;
                }
                setSnowReporter(value === "true");
              },
            },
          ]}
        />
      )}
      {isTrueEmpty ? (
        <Box mt="20px">
          <EmptyState
            title="No Customers Yet"
            description="New customers will appear here once you create them"
            actionLabel="Add Customer"
            actionStartIcon={<Add />}
            onAction={() => navigate(AdRoutes.addCustomerCombo)}
          />
        </Box>
      ) : isEmpty ? (
        <Box mt="20px">
          <EmptyState {...noResultsEmptyState("customers", clearFilters)} />
        </Box>
      ) : (
        <AddressManagementTableCombo
          data={data?.data}
          sortOptions={sortOptions}
          sortOptionsChange={setSortOptions}
          isLoading={isLoading}
          handleLocationSelection={handleCheckboxChange}
          handleLocationSelectionAll={handleCheckboxChangeForAll}
          selectedLocationIds={selectedLocationIds}
          onDownloadReport={openPlowReportForLocation}
        />
      )}
      {!isError && (
        <MoreResultsLink
          count={matchingLeadsCount}
          entityCategoryLabel="Leads"
          targetRoute={AdRoutes.leads}
          searchTerm={search}
        />
      )}
      <PlowReportDialog
        open={plowReport !== null}
        locationIds={plowReport?.locationIds ?? []}
        subtitle={plowReport?.subtitle}
        onDownloaded={() => {
          if (plowReport?.clearSelectionOnSuccess) {
            resetLocationSelections();
          }
        }}
        onClose={() => setPlowReport(null)}
      />
    </DashboardContentLayout>
  );
};

export default CustomerManagementComboPage;
